import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Duration } from 'effect';
import {
  documentInsightsFailureKind,
  documentInsightsSelection,
  loadSavedDocumentInsights,
  retrySavedDocumentInsights,
  savedDocumentInsightsKey,
} from './document-insights';
import { unwrapEffectQueryFailure } from './query-error';
import { useEq } from './runtime-context';

export function useSavedDocumentInsights({
  audioId,
  userId,
  enabled,
}: {
  audioId: string | undefined;
  userId: string | undefined;
  enabled: boolean;
}) {
  const eq = useEq();
  const queryClient = useQueryClient();
  const ready = audioId !== undefined && userId !== undefined;
  const queryKey = savedDocumentInsightsKey(
    userId ?? '',
    audioId ?? '',
    documentInsightsSelection,
  );
  const query = useQuery({
    ...eq.queryOptions({
      queryKey,
      queryFn: () =>
        loadSavedDocumentInsights(audioId ?? '', documentInsightsSelection),
    }),
    enabled: (query) =>
      ready && (enabled || query.state.data?.status === 'pending'),
    retry: false,
    staleTime: Duration.toMillis(Duration.minutes(1)),
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' && !query.state.error
        ? Duration.toMillis(Duration.seconds(2))
        : false,
  });
  const mutation = useMutation({
    ...eq.mutationOptions({ mutationFn: retrySavedDocumentInsights }),
    retry: false,
    onMutate: async (input) => {
      const startedKey = savedDocumentInsightsKey(
        userId ?? '',
        input.audioId,
        documentInsightsSelection,
      );
      await queryClient.cancelQueries({ queryKey: startedKey });
      return { queryKey: startedKey };
    },
    onSuccess: (saved, _input, started) =>
      queryClient.setQueryData(started.queryKey, saved),
  });
  const retryingThisDocument =
    mutation.isPending && mutation.variables?.audioId === audioId;
  const retryMatchesRecord =
    mutation.variables?.audioId === audioId &&
    mutation.variables?.retryRequestId === query.data?.requestId;
  const failure = unwrapEffectQueryFailure(
    (retryMatchesRecord ? mutation.error : null) ?? query.error,
  );
  const generate = () => {
    if (audioId === undefined || userId === undefined || retryingThisDocument)
      return false;
    if (query.data?.status !== 'failed') {
      mutation.reset();
      void query.refetch();
      return true;
    }
    mutation.mutate({ audioId, retryRequestId: query.data.requestId });
    return true;
  };
  return {
    ready,
    saved: query.data,
    loading: query.isFetching || retryingThisDocument,
    failureKind: failure ? documentInsightsFailureKind(failure) : null,
    generate,
  };
}
